import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@heidi/design-system/tabs';
import {
  TypographyCaption,
  TypographyH3,
  TypographyP2,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown, X } from 'lucide-react';
import { useState, type JSX } from 'react';
import { useIntl } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import { ObjectWorkspaceFacetContent } from './ObjectWorkspaceFacetContent';
import { ObjectWorkspacePatientIdentityRow } from './ObjectWorkspacePatientIdentityRow';
import {
  FACETS_BY_KIND,
  type ObjectWorkspaceAnchor,
  type ObjectWorkspaceDocumentsState,
  type ObjectWorkspaceFacet,
  type ObjectWorkspaceObject,
  type ObjectWorkspacePatientDetailsState,
  type ObjectWorkspacePatientSessionsState,
  type ObjectWorkspaceTask,
  type ObjectWorkspaceTasksState,
} from './objectWorkspace.data';

interface ObjectWorkspacePaneProps {
  object: ObjectWorkspaceObject;
  patientDetailsState?: ObjectWorkspacePatientDetailsState;
  patientSessionsState?: ObjectWorkspacePatientSessionsState;
  tasksState?: ObjectWorkspaceTasksState;
  documentsState?: ObjectWorkspaceDocumentsState;
  selectedTaskIds: readonly string[];
  /** Read-only host summary; blank hides the block. */
  summary?: string;
  /** Collapses the pane out of layout while the context panel owns the canvas. */
  isHidden?: boolean;
  switcherOptions: readonly ObjectWorkspaceObject[];
  onClose: () => void;
  onObjectChange: (object: ObjectWorkspaceObject) => void;
  onAddPatient?: (anchorElement: HTMLElement) => void;
  onFacetChange?: (facet: ObjectWorkspaceFacet) => void;
  onAnchorSelect?: (anchor: ObjectWorkspaceAnchor) => void;
  onDocumentSelect?: (document: ObjectWorkspaceAnchor) => void;
  onTaskToggle?: (task: ObjectWorkspaceTask) => void;
}

export function ObjectWorkspacePane({
  object,
  patientDetailsState,
  patientSessionsState,
  tasksState,
  documentsState,
  selectedTaskIds,
  summary = '',
  isHidden = false,
  switcherOptions,
  onClose,
  onObjectChange,
  onAddPatient,
  onFacetChange,
  onAnchorSelect,
  onDocumentSelect,
  onTaskToggle,
}: ObjectWorkspacePaneProps): JSX.Element {
  const intl = useIntl();
  const [activeFacet, setActiveFacet] = useState<ObjectWorkspaceFacet>('work');
  const facets = FACETS_BY_KIND[object.kind];
  const summaryText = summary.trim();
  const paneLabel = intl.formatMessage(
    object.kind === 'patient' ? messages.patientObjectPane : messages.visitObjectPane,
    { name: object.name }
  );

  return (
    <aside
      aria-label={paneLabel}
      aria-hidden={isHidden || undefined}
      className={cn(
        'relative size-full shrink-0 overflow-hidden bg-surface md:w-[36%] md:min-w-80 md:max-w-[440px] md:rounded-2xl md:shadow-sm',
        'motion-safe:transition-[width,min-width,max-width,margin,opacity,visibility] motion-safe:duration-300 motion-safe:ease-out',
        // Collapsed to nothing rather than `hidden` so width, margin and
        // opacity can tween; `invisible` flips only once the tween ends.
        isHidden &&
          'pointer-events-none invisible opacity-0 md:-me-4 md:!w-0 md:!min-w-0 md:!max-w-0'
      )}
      data-hidden={isHidden}
      data-testid="object-workspace-pane"
    >
      <Tabs
        value={activeFacet}
        onValueChange={(value) => {
          const facet = value as ObjectWorkspaceFacet;
          setActiveFacet(facet);
          onFacetChange?.(facet);
        }}
        className="scrollbar-subtle flex h-full min-h-full flex-col overflow-y-auto"
      >
        <div className="sticky top-0 z-10 bg-surface px-6 pb-4 pt-6 md:rounded-t-2xl">
          <div className="min-w-0">
            {object.kind === 'visit' ? (
              <TypographyCaption className="mb-1 text-text-tertiary">
                {intl.formatMessage(messages.encounterEyebrow)}
              </TypographyCaption>
            ) : null}
            <div className="flex min-w-0 items-start gap-1">
              <div
                className="flex min-w-0 items-start gap-1"
                data-testid="object-workspace-title-group"
              >
                <TypographyH3
                  className="min-w-0 truncate text-text-primary"
                  title={object.name}
                >
                  {object.name}
                </TypographyH3>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      iconOnly
                      className="shrink-0 [&_svg]:!size-5"
                      aria-label={intl.formatMessage(messages.switchObject, {
                        name: object.name,
                      })}
                    >
                      <ChevronDown aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-64">
                    {switcherOptions.map((option) => (
                      <DropdownMenuItem
                        key={option.id}
                        selected={option.id === object.id}
                        onSelect={() => onObjectChange(option)}
                      >
                        <span className="truncate">{option.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div
                className="ms-auto flex shrink-0 items-start gap-1"
                data-testid="object-workspace-header-actions"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={intl.formatMessage(messages.closeWorkspace)}
                  onClick={onClose}
                >
                  <X aria-hidden />
                </Button>
              </div>
            </div>
            {object.kind === 'visit' ? (
              <ObjectWorkspacePatientIdentityRow
                patient={object.patient}
                onObjectChange={onObjectChange}
                onAddPatient={onAddPatient}
              />
            ) : null}
          </div>

          {summaryText ? (
            <TypographyP2
              className="mt-3 line-clamp-6 whitespace-pre-line text-text-secondary"
              data-testid="object-workspace-summary"
            >
              {summaryText}
            </TypographyP2>
          ) : null}

          <div className="mt-8" data-testid="object-workspace-facet-tabs">
            <TabsList className="h-7 w-full justify-start gap-1 bg-transparent p-0">
              {facets.map((facet) => (
                <TabsTrigger
                  key={facet.id}
                  value={facet.id}
                  className="h-7 px-2 data-[state=active]:bg-background-secondary"
                >
                  {intl.formatMessage(facet.label)}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>

        {facets.map((facet) => (
          <TabsContent key={facet.id} value={facet.id} className="m-0 px-6 pb-8 pt-2">
            <ObjectWorkspaceFacetContent
              facet={facet.id}
              kind={object.kind}
              patientDetailsState={patientDetailsState}
              patientSessionsState={patientSessionsState}
              tasksState={tasksState}
              documentsState={documentsState}
              selectedTaskIds={selectedTaskIds}
              onAnchorSelect={onAnchorSelect}
              onDocumentSelect={onDocumentSelect}
              onTaskToggle={onTaskToggle}
            />
          </TabsContent>
        ))}
      </Tabs>
    </aside>
  );
}
